/**
 * Sequencer Renderer
 *
 * Creates the visual representation of the sequencer as nested spinning squares.
 * Each square rotates at a different speed - outer squares slower, inner squares faster.
 * Color updates dynamically to match the current block's miniblocks.
 *
 * This replaces the simple spawn indicator square below the bar chart.
 */

import { Container, Graphics, FederatedPointerEvent } from 'pixi.js';
import { getBlockColor } from '@/utils/blockColors';
import { ThemeManager } from './ThemeManager';
import type { PixiThemeColors } from '../config/themes';

export class SequencerRenderer {
  private squaresContainer: Container;
  private squares: Graphics[] = [];
  private currentBlockNumber: number = -1;
  private currentColor: number;

  // Square configuration
  private readonly NUM_LAYERS = 6;
  private readonly OUTER_SIZE = 42;  // Reduced from 50 to remove outermost layer effect
  private readonly INNER_SIZE = 6;   // Proportionally smaller
  private readonly BASE_SPEED = 0.5;  // rad/sec for outermost (idle speed)
  private readonly SPEED_MULTIPLIER = 1.4;  // Each inner layer is faster
  private readonly STROKE_WIDTH_OUTER = 2;
  private readonly STROKE_WIDTH_INNER = 1;
  private readonly CORNER_RADIUS_RATIO = 0.18;  // Corner radius as ratio of size

  // Dynamic speed configuration
  private readonly MAX_SPEED_MULTIPLIER = 25;  // Max speed is 25x base speed when busy
  private readonly SPEED_BOOST_PER_TX = 0.5;  // Speed boost per transaction hit
  private readonly DECELERATION_RATE = 8.0;  // How fast speed returns to base (per second) - fast decay

  // Animation state
  private rotations: number[] = [];
  private baseRotationSpeeds: number[] = [];  // Base speeds per layer (idle)
  private sizes: number[] = [];
  private speedMultiplier: number = 1.0;  // Current speed multiplier (1.0 = idle)

  // Click handling
  private hitArea: Graphics;
  private onClickCallback: ((mouseX: number, mouseY: number) => void) | null = null;

  // Theme colors (reactive)
  private colors: PixiThemeColors;
  private unsubscribeTheme: (() => void) | null = null;

  constructor(parentContainer: Container) {
    // Initialize theme colors
    this.colors = ThemeManager.colors;
    this.currentColor = this.colors.textSecondary;

    // Subscribe to theme changes
    this.unsubscribeTheme = ThemeManager.subscribe((colors) => {
      this.colors = colors;
      this.applyTheme();
    });

    // Create container for squares (will be positioned by setPosition)
    this.squaresContainer = new Container();
    this.squaresContainer.eventMode = 'static'; // Enable events on container
    parentContainer.addChild(this.squaresContainer);

    // Create invisible hit area for click detection
    this.hitArea = new Graphics();
    this.hitArea.circle(0, 0, this.OUTER_SIZE / 2 + 10); // Slightly larger than outer square
    this.hitArea.fill({ color: 0x000000, alpha: 0.001 }); // Nearly invisible
    this.hitArea.eventMode = 'static';
    this.hitArea.cursor = 'pointer';
    this.squaresContainer.addChild(this.hitArea);

    // Disable events on the squares container itself so only hitArea receives clicks
    this.squaresContainer.eventMode = 'passive';

    // Handle clicks on hit area
    this.hitArea.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation(); // Prevent event from bubbling to parent
      if (this.onClickCallback) {
        this.onClickCallback(e.globalX, e.globalY);
      }
    });

    // Initialize squares
    this.initializeSquares();
  }

  /**
   * Initialize all square layers with their sizes, speeds, and initial rotations
   */
  private initializeSquares(): void {
    for (let i = 0; i < this.NUM_LAYERS; i++) {
      // Calculate size (lerp from outer to inner)
      const t = i / (this.NUM_LAYERS - 1);
      const size = this.OUTER_SIZE * (1 - t) + this.INNER_SIZE * t;
      this.sizes.push(size);

      // Calculate base rotation speed (exponentially faster for inner squares)
      const speed = this.BASE_SPEED * Math.pow(this.SPEED_MULTIPLIER, i);
      this.baseRotationSpeeds.push(speed);

      // Stagger initial rotations for visual interest
      const initialRotation = (Math.PI / 4) * (i % 2) + (i * 0.15);
      this.rotations.push(initialRotation);

      // Create graphics for this square (no events - hitArea handles clicks)
      const square = new Graphics();
      square.eventMode = 'none';
      this.squaresContainer.addChild(square);
      this.squares.push(square);
    }

    // Draw initial state
    this.drawAllSquares();
  }

  /**
   * Draw a single square with the given parameters
   */
  private drawSquare(
    graphics: Graphics,
    size: number,
    rotation: number,
    color: number,
    strokeWidth: number
  ): void {
    graphics.clear();

    // Set rotation
    graphics.rotation = rotation;

    // Calculate corner radius based on size
    const radius = size * this.CORNER_RADIUS_RATIO;
    const half = size / 2;

    // Draw rounded rectangle centered at origin
    graphics.roundRect(-half, -half, size, size, radius);
    graphics.stroke({ width: strokeWidth, color });
  }

  /**
   * Draw all squares with current rotations and color
   */
  private drawAllSquares(): void {
    for (let i = 0; i < this.NUM_LAYERS; i++) {
      // Calculate stroke width (thicker for outer, thinner for inner)
      const t = i / (this.NUM_LAYERS - 1);
      const strokeWidth = this.STROKE_WIDTH_OUTER * (1 - t) + this.STROKE_WIDTH_INNER * t;

      this.drawSquare(
        this.squares[i],
        this.sizes[i],
        this.rotations[i],
        this.currentColor,
        strokeWidth
      );
    }
  }

  /**
   * Set the position of the sequencer (called by BarGraphRenderer)
   */
  public setPosition(x: number, y: number): void {
    this.squaresContainer.x = x;
    this.squaresContainer.y = y;
  }

  /**
   * Get current X position
   */
  public getX(): number {
    return this.squaresContainer.x;
  }

  /**
   * Get current Y position
   */
  public getY(): number {
    return this.squaresContainer.y;
  }

  /**
   * Update animation - call this every frame
   * @param delta - Frame delta in ms
   */
  public update(delta: number): void {
    // Convert delta to seconds
    const dt = delta / 1000;

    // Decelerate speed multiplier back towards 1.0 (idle)
    if (this.speedMultiplier > 1.0) {
      this.speedMultiplier -= this.DECELERATION_RATE * dt;
      if (this.speedMultiplier < 1.0) {
        this.speedMultiplier = 1.0;
      }
    }

    // Update each square's rotation using speed multiplier
    for (let i = 0; i < this.NUM_LAYERS; i++) {
      const currentSpeed = this.baseRotationSpeeds[i] * this.speedMultiplier;
      this.rotations[i] += currentSpeed * dt;

      // Keep rotation in reasonable bounds to avoid floating point issues
      if (this.rotations[i] > Math.PI * 2) {
        this.rotations[i] -= Math.PI * 2;
      }

      // Update the graphics rotation directly (more efficient than redrawing)
      this.squares[i].rotation = this.rotations[i];
    }
  }

  /**
   * Called when a transaction hits the sequencer - boosts spin speed
   * @param count - Number of transactions hitting (default 1)
   */
  public onTransactionHit(count: number = 1): void {
    this.speedMultiplier += this.SPEED_BOOST_PER_TX * count;
    if (this.speedMultiplier > this.MAX_SPEED_MULTIPLIER) {
      this.speedMultiplier = this.MAX_SPEED_MULTIPLIER;
    }
  }

  /**
   * Get the current speed multiplier (1.0 = idle, up to MAX_SPEED_MULTIPLIER when busy)
   * Used by BarGraphRenderer to sync beam intensity
   */
  public getSpeedMultiplier(): number {
    return this.speedMultiplier;
  }

  /**
   * Update sequencer color to match current block
   */
  public updateBlockColor(blockNumber: number): void {
    if (blockNumber === this.currentBlockNumber) return;

    this.currentBlockNumber = blockNumber;
    this.currentColor = getBlockColor(blockNumber);
    this.drawAllSquares();
  }

  /**
   * Set callback for when sequencer is clicked
   */
  public setClickCallback(callback: (mouseX: number, mouseY: number) => void): void {
    this.onClickCallback = callback;
  }

  /**
   * Apply theme colors to all visual elements
   */
  private applyTheme(): void {
    // If no block has been received yet, use theme secondary color
    if (this.currentBlockNumber === -1) {
      this.currentColor = this.colors.textSecondary;
      this.drawAllSquares();
    }
  }

  public destroy(): void {
    if (this.unsubscribeTheme) {
      this.unsubscribeTheme();
    }
    if (this.squaresContainer) {
      this.squaresContainer.destroy({ children: true });
    }
  }
}
